// ?use_ios_ui — twin of iOS 0.7.0 FramedPlacementOverlay: a ring the user
// aligns their rim in, a light scrim outside it, Place and Cancel. Drawn
// full-bleed in viewport coordinates so the ring sits exactly where
// placement raycasts — both read one TFramedReticle.

import { IOS_UI_FONT, IOS_UI_PALETTE } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import type { TFramedReticle } from '@/models/framed-reticle'

const SCRIM = 'rgba(255, 255, 255, 0.55)'
const PLACE_HEIGHT = 56
const CANCEL_HEIGHT = 44
const BUTTON_GAP = 8

interface FramedPlacementOverlayProps {
  reticle: TFramedReticle
  /** 1 for the first wheel, 2 for the second. */
  wheelIndex: number
  isLandscape: boolean
  onPlace: () => void
  onCancel: () => void
}

export function FramedPlacementOverlay({ reticle, wheelIndex, isLandscape, onPlace, onCancel }: FramedPlacementOverlayProps) {
  const { center, radius } = reticle
  const buttonWidth = isLandscape ? 160 : radius * 2
  // Portrait: under the hint. Landscape: right of the ring.
  const placeCenter = isLandscape
    ? { x: center.x + radius + 24 + buttonWidth / 2, y: center.y }
    : { x: center.x, y: center.y + radius + 110 }
  // Cancel hangs below Place, so Place keeps its iOS position and the
  // stack's top is Place's top.
  const stackTop = placeCenter.y - PLACE_HEIGHT / 2
  return (
    <div data-testid="ios-framed-overlay" style={{ position: 'fixed', inset: 0, fontFamily: IOS_UI_FONT, pointerEvents: 'none', zIndex: 10 }}>
      {/* Everything outside the ring dimmed; the ring itself clear. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: SCRIM,
          WebkitMaskImage: `radial-gradient(circle ${radius}px at ${center.x}px ${center.y}px, transparent ${radius}px, #000 ${radius + 0.5}px)`,
          maskImage: `radial-gradient(circle ${radius}px at ${center.x}px ${center.y}px, transparent ${radius}px, #000 ${radius + 0.5}px)`,
        }}
      />
      <div
        data-testid="ios-framed-ring"
        style={{
          position: 'absolute',
          left: `${center.x - radius}px`,
          top: `${center.y - radius}px`,
          width: `${radius * 2}px`,
          height: `${radius * 2}px`,
          boxSizing: 'border-box',
          borderRadius: '50%',
          border: `4px solid ${IOS_UI_PALETTE.accent}`,
        }}
      />
      <div
        data-testid="ios-framed-title"
        style={{
          position: 'absolute',
          left: center.x,
          top: center.y - radius - (isLandscape ? 36 : 70),
          transform: 'translate(-50%, -50%)',
          fontSize: isLandscape ? 22 : 30,
          color: '#000',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          lineHeight: 1.15,
        }}
      >
        Frame your
        <br />
        {wheelIndex === 1 ? 'first wheel' : 'second wheel'}
      </div>
      {!isLandscape && (
        <div
          data-testid="ios-framed-hint"
          style={{
            position: 'absolute',
            left: center.x,
            top: center.y + radius + 40,
            transform: 'translate(-50%, -50%)',
            fontSize: 16,
            color: '#000',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Once your wheel is aligned,
          <br />
          press “Place”
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: placeCenter.x - buttonWidth / 2,
          top: stackTop,
          width: buttonWidth,
          display: 'flex',
          flexDirection: 'column',
          gap: BUTTON_GAP,
          pointerEvents: 'auto',
        }}
      >
        <button
          type="button"
          data-testid="ios-framed-place"
          aria-label="Place wheel"
          onClick={onPlace}
          style={{
            height: PLACE_HEIGHT,
            border: 'none',
            borderRadius: PLACE_HEIGHT / 2,
            background: IOS_UI_PALETTE.accent,
            color: IOS_UI_PALETTE.onAccent,
            fontSize: 18,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Place
        </button>
        <button
          type="button"
          data-testid="ios-framed-cancel"
          aria-label="Cancel placement"
          onClick={onCancel}
          style={{
            height: CANCEL_HEIGHT,
            border: 'none',
            borderRadius: CANCEL_HEIGHT / 2,
            background: 'rgba(255, 255, 255, 0.75)',
            color: 'rgba(0, 0, 0, 0.75)',
            fontSize: 17,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
